import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import {
  LayoutDashboard, Package, Truck, FileText, ShoppingCart,
  Users, LogOut, ChevronDown, Building2, AlertTriangle, Shield, BarChart2, RefreshCw, Layers, TrendingUp, Gift, Eye, Store, Upload, LineChart, ArrowLeftRight, MapPin, HelpCircle, UserPlus, Sun, Moon
} from 'lucide-react'
import { useState } from 'react'
import './Layout.css'
import IdleSignOut from '../IdleSignOut'
import { getTheme, setTheme } from '../../lib/theme'

const NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', hideForBrandAdmin: true, end: true },
  { to: '/catalog', icon: Package, label: 'Catalog' },
  { to: '/suppliers', icon: Building2, label: 'Suppliers' },
  { to: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders' },
  { to: '/wholesale-orders', icon: Store, label: 'Wholesale Orders', hideForBrandAdmin: true },
  { to: '/shipments', icon: Truck, label: 'Shipments' },
  { to: '/invoices', icon: FileText, label: 'Invoices', hideForBrandAdmin: true },
  { to: '/inventory', icon: BarChart2, label: 'Inventory' },
  { to: '/planner', icon: LineChart, label: 'Planner', brandSlugs: ['bloomi'] },
  { to: '/planning-inputs', icon: Upload, label: 'Planning Inputs', brandSlugs: ['bloomi'] },
  { to: '/assembly-orders', icon: Layers, label: 'Assembly Orders', hideForBrandAdmin: true },
  { to: '/subscription-boxes', icon: Gift, label: 'Subscription Boxes', brandSlugs: ['suzanne-somers'] },
  { to: '/sales', icon: TrendingUp, label: 'Sales', hideForBrandAdmin: true },
  // Own pages for brand_admins only (super admins use the Inventory tabs)
  { to: '/transfer-orders', icon: ArrowLeftRight, label: 'Transfer Orders', brandAdminOnly: true },
  { to: '/locations', icon: MapPin, label: 'Locations', brandAdminOnly: true },
  // brand_admins invite teammates here; super admins use Users
  { to: '/invite', icon: UserPlus, label: 'Team', brandAdminOnly: true },
]
// Sidebar order for brand_admins (decided Oct 2026); anything not listed keeps its place after these
const BRAND_ADMIN_ORDER = ['/planner', '/planning-inputs', '/inventory', '/purchase-orders', '/shipments', '/transfer-orders', '/catalog', '/suppliers', '/locations', '/invite']
const baRank = to => { const i = BRAND_ADMIN_ORDER.indexOf(to); return i === -1 ? BRAND_ADMIN_ORDER.length : i }

export default function Layout() {
  const { profile, actualRole, accessibleBrands, activeBrand, setActiveBrand, signOut, viewAsBrandAdmin, setViewAsBrandAdmin } = useAuth()
  const [brandOpen, setBrandOpen] = useState(false)
  const [theme, setThemeState] = useState(getTheme)
  const toggleTheme = () => { const next = theme === 'dark' ? 'light' : 'dark'; setTheme(next); setThemeState(next) }
  const navigate = useNavigate()

  const brandColors = {
    'bloomi': '#c47dd8',
    'wamsutta': '#6c8ef7',
    'suzanne-somers': '#4caf7d',
    'hanover': '#f08c4a',
    'home-textiles': '#6c8ef7',
    'health-beauty': '#4caf7d',
    'intimates': '#c47dd8'
  }

  const color = activeBrand ? (brandColors[activeBrand.slug] || '#6c8ef7') : '#6c8ef7'

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="app-brand">
            <img
              src="/Decent-Startup_Gmail-Symbol_2x.png"
              alt=""
              style={{ width: '32px', height: '32px', filter: theme === 'dark' ? 'invert(1)' : 'none', flexShrink: 0 }}
            />
            <div>
              <div className="app-name">Inventory</div>
              <div className="app-sub">by Decent Startup</div>
            </div>
          </div>

          {/* Brand Switcher */}
          <div className="brand-switcher">
            <button
              className="brand-btn"
              onClick={() => setBrandOpen(o => !o)}
              style={{ '--brand-color': color }}
            >
              <div className="brand-dot" style={{ background: color }} />
              <span className="brand-name">{activeBrand?.name || 'Select Brand'}</span>
              <ChevronDown size={14} className={`brand-chevron ${brandOpen ? 'open' : ''}`} />
            </button>
            {brandOpen && (
              <div className="brand-dropdown">
                {accessibleBrands.map(b => (
                  <button
                    key={b.id}
                    className={`brand-option ${activeBrand?.id === b.id ? 'active' : ''}`}
                    onClick={() => { setActiveBrand(b); setBrandOpen(false) }}
                  >
                    <div className="brand-dot" style={{ background: brandColors[b.slug] || '#6c8ef7' }} />
                    {b.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View-as toggle (super_admin + brand_super_admin) */}
          {(actualRole === 'super_admin' || actualRole === 'brand_super_admin') && (
            <label className={`view-as-toggle ${viewAsBrandAdmin ? 'on' : ''}`} title="QA the brand_admin UX without changing your real role">
              <Eye size={13} />
              <span className="view-as-label">View as brand_admin</span>
              <span className="view-as-switch" aria-hidden="true">
                <span className="view-as-knob" />
              </span>
              <input
                type="checkbox"
                checked={viewAsBrandAdmin}
                onChange={e => setViewAsBrandAdmin(e.target.checked)}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              />
            </label>
          )}

          {/* Nav */}
          <nav className="sidebar-nav">
            {NAV.filter(item => (!item.brandSlugs || item.brandSlugs.includes(activeBrand?.slug)) && (profile?.role === 'brand_admin' ? !item.hideForBrandAdmin : !item.brandAdminOnly))
              .sort((a, b) => (profile?.role === 'brand_admin' ? baRank(a.to) - baRank(b.to) : 0)).map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={16} />
                {label}
              </NavLink>
            ))}
            {(profile?.role === 'super_admin' || profile?.role === 'brand_super_admin') && (
              <>
                {profile?.role === 'super_admin' && (
                  <NavLink to="/users" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <Users size={16} />
                    Users
                  </NavLink>
                )}
                <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <Shield size={16} />
                  Admin
                </NavLink>
                <NavLink to="/shopify-sync" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <RefreshCw size={16} />
                  Shopify Sync
                </NavLink>
                <NavLink to="/lot-import" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <Layers size={16} />
                  Lot Import
                </NavLink>
              </>
            )}
            {/* Help is last for every role */}
            <NavLink to="/help" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <HelpCircle size={16} />
              Help
            </NavLink>
          </nav>
        </div>

        <div className="sidebar-bottom">
          <div className="user-info">
            <div className="user-avatar">{profile?.full_name?.[0] || profile?.email?.[0] || '?'}</div>
            <div className="user-details">
              <div className="user-name">{profile?.full_name || 'User'}</div>
              <div className="user-role">{profile?.role?.replace('_', ' ')}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} aria-label="Toggle light or dark mode">
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={handleSignOut} title="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        {!activeBrand ? (
          <div className="empty-state">
            <AlertTriangle size={40} />
            <p>No brand assigned to your account. Contact your admin.</p>
          </div>
        ) : (
          <div className="content-inner">
            <Outlet />
            <IdleSignOut />
          </div>
        )}
      </main>
    </div>
  )
}
